import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { UserStorageService } from '../services/storage/user-storage.service';
import { Trip } from './trip-models/trip.model';
import { UserData } from './user-models/user-data.model';

@Injectable({
  providedIn: 'root',
})
export class RouteGuard {

  // data preambles
  requestedRoute: string = '';
  userDetails: UserData = new UserData();
  currTrip: Trip = new Trip();

  // flags that tell us if a routing condition is true or false
  hasLogbooks: boolean = false;
  noTripHome: boolean = false;
  activeTripHome: boolean = false;
  offloadHome: boolean = false;
  activityRecorded: boolean = false;
  offloadNeeded: boolean = false;
  gillNetTrip: boolean = false;
  seineTrip: boolean = false;
  trollTrip: boolean = false;
  isEditSubmission: boolean = false;

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router
  ) {

  }

  // determine if the user can navigate to the requested page, invoked in app-routing.module.ts
  canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {

    // read while the navigation is still current -- the guard's checks are async and
    // getCurrentNavigation() is null again by the time they finish
    this.isEditSubmission = (this.router.getCurrentNavigation()?.extras.state as any)?.isEditSubmission === true;

    return new Promise<boolean>((res) => {

      this.authenticationService.getUserDetails().then(() => {
        this.userDetails = UserStorageService.currUser;
        if (!this.userDetails.isValid() && !UserStorageService.adminUser) {  // if the user is not authd/signed-up
          this.authenticationService.logout(); // redirect the user to the login page
          res(false); // return false to stop the requested routing 
        }
      }).then(() => {
        if (UserStorageService.adminUser && UserStorageService.adminUser.adminReplicated == '') res(true);
        else {
          UserStorageService.getCurrTrip().then(() => {
            this.currTrip = UserStorageService.currTrip;
            this.initialize(route); // initialize the needed variables for route logic
            if (!this.evaluateGuard()) { // if the requested page is not accessible
              this.authenticationService.logout(); // redirect the user to the login page
              res(false);  // return false to stop the requested routing
            } 
            res(true); // if the page is deemed accessible, return true to complete the requested routing
          });
        }
        
      });

    });

  }

  // initialize the needed variables for route logic
  initialize(route: ActivatedRouteSnapshot): void {

    if (route.routeConfig) {
      this.requestedRoute = route.routeConfig.path ?? '';
    }

    this.hasLogbooks = (this.userDetails.logbooks.length > 0);
    this.noTripHome = (this.userDetails.currHomepage === 'no-trip-home');
    this.activeTripHome = (this.userDetails.currHomepage === 'active-trip-home');
    this.offloadHome = (this.userDetails.currHomepage === 'offload-home');
    this.activityRecorded = (this.currTrip.dailyCatchReports.length > 0 || this.currTrip.pauseFishingReports.length > 0);
    this.offloadNeeded = this.currTrip.offloadNeeded;
    this.gillNetTrip = (this.currTrip.logbook.gearName === 'Gill Net');
    this.seineTrip = (this.currTrip.logbook.gearName === 'Seine');
    this.trollTrip = (this.currTrip.logbook.gearName === 'Troll');

  }

  // performs the necessary logic for each route and returns true if the page is accessible or false if not
  evaluateGuard(): boolean {

    // Please the 'Route Guards' page of the Salmon eLog product documentation in the IT Notebook
    // for a written description of the logic performed for each page.

    switch (this.requestedRoute) {

      case 'active-trip-home':
        return this.activeTripHome;
      case 'offload-home':
        return this.offloadHome;
      case 'no-trip-home':
        return this.noTripHome;
      case 'start-report':
        return this.noTripHome && this.hasLogbooks;
      case 'pause-report':
        return this.activeTripHome;
      case 'end-report':
        return this.activeTripHome && this.activityRecorded;
      case 'cancel-report':
        return this.activeTripHome && !this.activityRecorded;
      case 'offload-report':
        return (this.activeTripHome || this.offloadHome) && this.offloadNeeded && this.activityRecorded;
      case 'gill-net-report':
        return this.activeTripHome && this.gillNetTrip;
      case 'seine-report':
        return this.activeTripHome && this.seineTrip;
      case 'troll-report':
        return this.activeTripHome && this.trollTrip;
      case 'submission-page':
        // an edit filed from the records page belongs to whichever trip was edited, which is
        // usually a finished one -- there is no active trip to validate against by then
        return this.isEditSubmission || this.currTrip.isValid();
      case 'admin-panel':
        return true; //if user is admin
        
    }

    return true; // records-page, resources, fishery-notices, profile

  }


}
